import React from "react";
import { useFormContext } from "react-hook-form";
import StepHeader from "../StepHeader";
import RHFInput from "@/components/form/RHFInput";
import RHFTextArea from "@/components/form/RHFTextArea";
import { ALTERNATE_EMAIL_MAX_LENGTH, validateAlternateEmail } from "@/utils/alternateEmail";

const DEFAULT_REQUIRED = ["name", "email", "phone", "location"];

const StepPersonal = ({ mandatoryFields = [], lockedSections = [], disabled = false, alternateEmailLabel = "Alternate email (optional)", alternateEmailHelperText }) => {
  const infoLocked = disabled || lockedSections.includes("personal_information");
  const summaryLocked = disabled || lockedSections.includes("summary");
  const { watch } = useFormContext();
  const primaryEmail = watch("personal_information.email");

  const isFieldRequired = (fieldNames) => {
    if (DEFAULT_REQUIRED.some((f) => fieldNames.includes(f))) return true;
    return mandatoryFields.some((f) => fieldNames.some((fn) => f.endsWith(fn) || f === fn));
  };

  const isNameRequired = isFieldRequired(["personal_information.full_name", "personal_information.name", "name"]);
  const isEmailRequired = isFieldRequired(["personal_information.email", "email"]);
  const isPhoneRequired = isFieldRequired(["personal_information.phone", "phone"]);
  const isLocationRequired = isFieldRequired(["personal_information.location", "location"]);
  const isLinkedinRequired = isFieldRequired(["personal_information.linkedin", "linkedin"]);
  const isWebsiteRequired = isFieldRequired(["personal_information.website", "website"]);
  const isSummaryRequired = isFieldRequired(["summary", "personal_information.summary"]);

  return (
    <div>
      <StepHeader title="Personal Information" />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 px-3 md:px-5">
        <RHFInput name="personal_information.name" label="Full Name" placeholder="Enter full name" required={isNameRequired} disabled={infoLocked} />
        <RHFInput name="personal_information.email" label="Email" type="email" placeholder="Enter your Email ID" required={isEmailRequired} disabled={infoLocked} />

        <div className="md:col-span-2">
          <RHFInput
            name="personal_information.alternate_email"
            label={alternateEmailLabel}
            type="email"
            placeholder="Enter an alternate email"
            maxLength={ALTERNATE_EMAIL_MAX_LENGTH}
            disabled={infoLocked}
            rules={{
              validate: (value) => validateAlternateEmail({ alternateEmail: value, primaryEmail }) ?? true,
            }}
          />
          {alternateEmailHelperText && <p className="mt-1 text-xs text-text-secondary">{alternateEmailHelperText}</p>}
        </div>

        <div>
          <RHFInput
            name="personal_information.phone"
            label="Phone"
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            placeholder="e.g. +44 7911 123456 or 44 7911 123456"
            required={isPhoneRequired}
            disabled={infoLocked}
          />
          <p className="mt-1 text-xs text-text-secondary">
            For international numbers, include the country code with or without +. Local numbers are also accepted when valid.
          </p>
        </div>
        <RHFInput
          name="personal_information.location"
          label="Current Location"
          placeholder="Enter your Location"
          required={isLocationRequired}
          disabled={infoLocked}
        />

        <div className="md:col-span-2">
          <RHFInput name="personal_information.linkedin" label="LinkedIn" placeholder="linkedin.com/in/..." required={isLinkedinRequired} disabled={infoLocked} />
        </div>
        <div className="md:col-span-2">
          <RHFInput name="personal_information.portfolio" label="Portfolio / Website" prefix="https://" placeholder="yourportfolio.com" required={isWebsiteRequired} disabled={infoLocked} />
        </div>

        <div className="md:col-span-2">
          <RHFTextArea
            name="personal_information.summary"
            label="Professional Summary / Objective"
            rows={5}
            placeholder="Write a brief professional summary..."
            required={isSummaryRequired}
            disabled={summaryLocked}
            autoResize
          />
        </div>
      </div>
    </div>
  );
};

export default StepPersonal;
